import React from 'react';
import DatePicker from 'react-datepicker';
import { getYear, getMonth, isValid, format } from 'date-fns';
import {
  Select,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
} from '@chakra-ui/react';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
//import { format, parseISO, isValid } from 'date-fns';

const currentBuddhistYear = new Date().getFullYear() + 543;
const years = Array.from({ length: 91 }, (_, i) => currentBuddhistYear - 80 + i);
const months = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
];

// ***************************************************************
// ฟังก์ชันสำหรับแปลงวันที่ (เพิ่มเข้ามาใหม่)
export const convertToThDate = (inputDate) => {
  let date;

  // ตรวจสอบประเภทของ input
  if (inputDate instanceof Date) {
    date = inputDate;
  } else if (typeof inputDate === 'string' && inputDate.includes('-')) {
    // แยกส่วนวันที่จาก string (YYYY-MM-DD)
    const datePart = inputDate.split('T')[0]; // ดึงเฉพาะส่วนวันที่
    const [year, month, day] = datePart.split('-').map(Number);
    date = new Date(year, month - 1, day); // month - 1 เพราะ JavaScript ใช้ 0-indexed
  } else {
    return ''; // คืนค่าว่างถ้า input ไม่ถูกต้อง
  }

  // ตรวจสอบว่า date เป็น Date object ที่ถูกต้อง
  if (!isValid(date)) {
    return '';
  }

  const displayDay = format(date, 'dd'); // จะได้วันตามที่ต้องการ (01)
  const displayMonth = format(date, 'MM'); // จะได้เดือนตามที่ต้องการ (07)
  const yearBuddhist = getYear(date) + 543;

  return `${displayDay}/${displayMonth}/${yearBuddhist}`;
};

// ฟังก์ชันสำหรับจัดการ ISO string โดยไม่สนใจ Timezone
export const getDateFromISOStringIgnoringTimezone = (isoString) => {
  if (!isoString) return null;
  const datePart = isoString.split('T')[0]; // ดึงเฉพาะส่วนวันที่ YYYY-MM-DD
  const [year, month, day] = datePart.split('-').map(Number);
  // สร้าง Date object โดยใช้ส่วนวันที่เท่านั้น เพื่อให้เป็น 00:00:00 ของวันนั้นๆ ใน Timezone ท้องถิ่น
  const date = new Date(year, month - 1, day); 
  return isValid(date) ? date : null;
};

// ***************************************************************

const CustomInput = React.forwardRef(({ onClick, displayValue, ...rest }, ref) => (
  <InputGroup>
    <Input
      {...rest}
      ref={ref}
      value={displayValue}
      onClick={onClick}
      readOnly
      placeholder={rest.monthOnly ? "เดือน/ปปปป" : "วว/ดด/ปปปป"}
      cursor="pointer"
      autoComplete="off"
    />
    <InputRightElement onClick={onClick} cursor="pointer">
      <FaCalendarAlt color="#808080" />
    </InputRightElement>
  </InputGroup>
));

const CustomDatePicker = ({ value, onChange, name, minDate, maxDate, monthOnly = false, popperPlacement = "auto", ...rest }) => {
  let selectedDate = null;
  if (value instanceof Date && isValid(value)) {
    selectedDate = value;
  } else if (typeof value === 'string' && isValid(getDateFromISOStringIgnoringTimezone(value))) {
    selectedDate = getDateFromISOStringIgnoringTimezone(value);
  }

  // เปลี่ยนการแสดงผลตามโหมด
  const displayValueForInput = selectedDate
    ? monthOnly
      ? `${months[getMonth(selectedDate)]} ${getYear(selectedDate) + 543}`
      : format(selectedDate, 'dd/MM/') + (getYear(selectedDate) + 543)
    : '';

  const handleDateChange = (date) => {
    const gregorianValue = date ? format(date, 'yyyy-MM-dd') : '';
    const event = {
      target: {
        name: name,
        value: gregorianValue,
      },
    };
    onChange(event);
  };

  return (
    <DatePicker
      selected={selectedDate}
      onChange={handleDateChange}
      minDate={minDate}
      maxDate={maxDate}
      showMonthYearPicker={monthOnly} // เปลี่ยนโหมดปฏิทิน
      customInput={<CustomInput displayValue={displayValueForInput} monthOnly={monthOnly} {...rest} />}
      renderCustomHeader={({
        date,
        changeYear,
        changeMonth,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
      }) => (
        <HStack justifyContent="space-between" p={2} bg="gray.50">
          <IconButton
            aria-label="Previous Month"
            icon={<FaChevronLeft />}
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
            size="sm"
          />
          <HStack>
            {/* ถ้าไม่ใช่โหมดเลือกเดือนอย่างเดียว ถึงจะแสดง dropdown เดือน */}
            {!monthOnly && (
              <Select
                size="sm"
                width="110px"
                value={months[getMonth(date)]}
                onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
              >
                {months.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </Select>
            )}
            <Select
              size="sm"
              width="80px"
              value={getYear(date) + 543}
              onChange={({ target: { value } }) => changeYear(value - 543)}
            >
              {years.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </Select>
          </HStack>
          <IconButton
            aria-label="Next Month"
            icon={<FaChevronRight />}
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
            size="sm"
          />
        </HStack>
      )}
      formatWeekDay={nameOfDay => nameOfDay.substring(0, 1)}
    />
  );
};

export default CustomDatePicker;